import React from "react";
import { motion } from "framer-motion";
import { Plane, Navigation } from "lucide-react";

/**
 * WOW animated city-map route scene (Dark Lime).
 * - faint tech grid + city blocks
 * - multiple route lines, one lime primary route that "draws" in
 * - a glowing car marker travels along the route (SMIL animateMotion, 60fps, GPU)
 * - pulsing geolocation pins at pickup & airport
 */
const MAIN_PATH = "M70 360 C 150 330, 150 230, 250 215 S 400 180, 430 90";
const ALT_PATH1 = "M40 250 C 140 240, 200 300, 320 300 S 470 280, 500 250";
const ALT_PATH2 = "M90 120 C 180 150, 260 110, 340 150 S 460 200, 500 160";

export const HeroScene = () => {
  return (
    <div className="relative w-full overflow-hidden rounded-[22px] border border-border bg-card flat-card" data-testid="hero-scene">
      <svg viewBox="0 0 520 420" className="w-full h-auto block" role="img" aria-label="Interactive city map with a car driving a route to the airport">
        <defs>
          <linearGradient id="limeGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#B8FF3B" />
            <stop offset="100%" stopColor="#7FE02E" />
          </linearGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* base */}
        <rect x="0" y="0" width="520" height="420" fill="#0d1220" />

        {/* grid */}
        <g stroke="#9db0ff" strokeOpacity="0.06" strokeWidth="1">
          {Array.from({ length: 12 }).map((_, i) => (
            <line key={`v${i}`} x1={i * 44} y1="0" x2={i * 44} y2="420" />
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={`h${i}`} x1="0" y1={i * 44} x2="520" y2={i * 44} />
          ))}
        </g>

        {/* city blocks */}
        <g fill="#141b2e">
          <rect x="120" y="150" width="60" height="60" rx="6" />
          <rect x="300" y="250" width="70" height="55" rx="6" />
          <rect x="210" y="300" width="50" height="60" rx="6" />
          <rect x="360" y="150" width="55" height="48" rx="6" />
          <rect x="60" y="250" width="48" height="52" rx="6" />
        </g>
        <g fill="#182138">
          <rect x="128" y="158" width="18" height="18" rx="3" />
          <rect x="154" y="158" width="18" height="18" rx="3" />
          <rect x="308" y="258" width="20" height="18" rx="3" />
          <rect x="336" y="258" width="20" height="18" rx="3" />
        </g>

        {/* alt routes (faint) */}
        <path d={ALT_PATH1} fill="none" stroke="#263150" strokeWidth="3" strokeLinecap="round" strokeDasharray="2 10" />
        <path d={ALT_PATH2} fill="none" stroke="#263150" strokeWidth="3" strokeLinecap="round" strokeDasharray="2 10" />

        {/* main lime route: draw in */}
        <motion.path
          d={MAIN_PATH}
          fill="none"
          stroke="url(#limeGrad)"
          strokeWidth="4"
          strokeLinecap="round"
          filter="url(#glow)"
          initial={{ pathLength: 0, opacity: 0.2 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: "easeInOut" }}
        />

        {/* pickup pin */}
        <g>
          <motion.circle cx="70" cy="360" r="10" fill="#B8FF3B" fillOpacity="0.25"
            initial={{ r: 10, opacity: 0.5 }}
            animate={{ r: [10, 22, 10], opacity: [0.5, 0, 0.5] }} transition={{ duration: 2, repeat: Infinity }} />
          <circle cx="70" cy="360" r="6" fill="#B8FF3B" filter="url(#glow)" />
          <circle cx="70" cy="360" r="2.4" fill="#0b0f19" />
        </g>

        {/* airport node */}
        <g>
          <motion.circle cx="430" cy="90" r="12" fill="#B8FF3B" fillOpacity="0.2"
            initial={{ r: 12, opacity: 0.5 }}
            animate={{ r: [12, 26, 12], opacity: [0.5, 0, 0.5] }} transition={{ duration: 2.2, repeat: Infinity, delay: 0.4 }} />
          <circle cx="430" cy="90" r="16" fill="#141b2e" stroke="#B8FF3B" strokeOpacity="0.5" />
          <g transform="translate(430,90)" stroke="#B8FF3B" strokeWidth="1.6" fill="none">
            <path d="M-6 1 L5 -2 Q8 -3 9 -1 L1 2 L-3 7 L-5 7 L-3 2 L-7 3 L-8 5 L-9.5 5 L-9 2 L-9.5 -1 L-8 -1 L-7 1 Z" fill="#B8FF3B" stroke="none" />
          </g>
        </g>

        {/* traveling car marker along the route */}
        <g filter="url(#glow)">
          <g>
            <g transform="translate(-9,-5)">
              <rect x="0" y="0" width="18" height="10" rx="3.5" fill="#B8FF3B" />
              <rect x="3" y="2.2" width="7" height="5.6" rx="1.6" fill="#0b0f19" fillOpacity="0.5" />
            </g>
            <animateMotion dur="5.5s" repeatCount="indefinite" rotate="auto" keyTimes="0;1" calcMode="linear" path={MAIN_PATH} />
          </g>
        </g>

        {/* floating particles */}
        {[
          { x: 200, y: 90, d: 3 }, { x: 410, y: 300, d: 4 }, { x: 110, y: 330, d: 3.5 },
        ].map((p, i) => (
          <motion.circle key={i} cx={p.x} cy={p.y} r="2" fill="#B8FF3B"
            animate={{ opacity: [0.2, 1, 0.2], y: [p.y, p.y - 8, p.y] }}
            transition={{ duration: p.d, repeat: Infinity, ease: "easeInOut" }} />
        ))}

        {/* HUD label */}
        <g>
          <rect x="300" y="340" width="178" height="52" rx="12" fill="#0b0f19" stroke="#223049" />
          <circle cx="322" cy="366" r="12" fill="#B8FF3B" fillOpacity="0.14" />
          <g transform="translate(316,360)"><path d="M6 0a6 6 0 1 0 0 12A6 6 0 0 0 6 0Z" fill="none" /></g>
          <text x="342" y="361" fill="#8aa0c2" fontSize="10" fontFamily="Manrope, sans-serif">Live route</text>
          <text x="342" y="378" fill="#ffffff" fontSize="13" fontWeight="700" fontFamily="Manrope, sans-serif">Praha → Letiště</text>
        </g>
      </svg>

      {/* overlay chips */}
      <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-background/70 border border-border px-3 py-1 text-[11px] text-foreground/80">
        <Navigation className="h-3 w-3 text-[hsl(var(--lime))]" /> GPS tracking
      </div>
      <div className="pointer-events-none absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-background/70 border border-border px-3 py-1 text-[11px] text-foreground/80">
        <Plane className="h-3 w-3 text-[hsl(var(--lime))]" /> Airport 24/7
      </div>
    </div>
  );
};
